import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {createGateway} from './InstrumentGatewayDAO';

function getBucketPolicy(bucketName: string, arnRole: string): string {
  return JSON.stringify(
    {
      Sid: 'BenchlingConnect',
      Effect: 'Allow',
      Principal: {
        AWS: arnRole,
      },
      Action: ['s3:ListBucket', 's3:GetObject', 's3:PutObject'],
      Resource: [`arn:aws:s3:::${bucketName}`, `arn:aws:s3:::${bucketName}/*`],
      Condition: {
        StringEquals: {
          'aws:PrincipalTag/TenantId': BenchlingConfig.TENANT_ID,
        },
      },
    },
    null,
    2
  );
}

function openPolicyTab(bucketName: string, roleArn: string): undefined {
  const bucketPolicyExample = new Blob([getBucketPolicy(bucketName, roleArn)], {type: 'application/json'});
  const url = window.URL.createObjectURL(bucketPolicyExample);
  window.open(url, '_blank', 'noopener noreferrer');
}

interface ViewBucketInfoModalProps {
  roleArn: string;
  onRequestClose: () => void;
  gatewayName: string;
  bucketName: string;
}

function isValidBucket(bucket: string): boolean {
  const regex = /^[a-zA-Z0-9.\-_]{1,255}$/;
  return regex.test(bucket);
}

export function ViewBucketInfoModal(props: ViewBucketInfoModalProps): JSX.Element {
  const {bucketName, roleArn, onRequestClose} = props;

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Generate policy',
          disabled: !bucketName,
          onClick: () => {
            openPolicyTab(bucketName, roleArn);
          },
          submit: false,
        },
        {
          style: ButtonStyle.PRIMARY,
          text: 'Close',
          cancel: true,
        },
      ]}
      title="S3 Bucket information"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
    >
      The following cloud gateway is connected to an AWS S3 bucket.
      <br />
      <br />
      <S3GatewayModalFields
        roleArn={roleArn}
        setGatewayName={() => {}}
        setBucketName={() => {}}
        gatewayName={props.gatewayName}
        bucketName={props.bucketName}
        viewOnly={true}
      />
    </Modal>
  );
}

interface CreateS3GatewayModalProps {
  roleArn: string;
  onRequestClose: () => void;
}

const S3BucketNamingNotice: React.ReactNode = (
  <p>
    The S3 bucket name does not comply with AWS naming rules. Please refer to the AWS bucket naming guidelines
    at:{' '}
    <a
      href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/bucketnamingrules.html"
      target="_blank"
      rel="noopener noreferrer"
    >
      AWS Bucket Naming Rules
    </a>
    .
  </p>
);

export function CreateS3GatewayModal(props: CreateS3GatewayModalProps): JSX.Element {
  const {roleArn, onRequestClose} = props;
  const [hasError, setHasError] = useState(false);

  const [gatewayName, setGatewayName] = useState('');
  const [bucketName, setBucketName] = useState('');

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Generate policy',
          disabled: !bucketName,
          onClick: () => {
            openPolicyTab(bucketName, roleArn);
          },
          submit: false,
        },
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !gatewayName || !bucketName,
          submit: true,
        },
      ]}
      title="Configure cloud gateway"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
      onRequestSubmit={async () => {
        if (!isValidBucket(bucketName.trim())) {
          setHasError(true);
          return;
        }
        await withLoadingScreen(
          // Trim surrounding whitespace from bucket name
          createGateway(gatewayName, 'S3', {customer_bucket: bucketName.trim()}),
          'Setting up instrument gateway...'
        );
        onRequestClose();
      }}
    >
      <div>
        <div>
          To connect to your S3 bucket, the following steps are required:
          <br />
          <br />
          1. Enter a Gateway Name for identification in Benchling
          <br />
          2. Provide your S3 Bucket Name
          <br />
          3. Generate and Apply the Policy to your Bucket
        </div>
        <br />
        <div className={css(styles.spacer)}>
          <S3GatewayModalFields
            roleArn={roleArn}
            setGatewayName={setGatewayName}
            setBucketName={setBucketName}
            gatewayName={gatewayName}
            bucketName={bucketName}
            viewOnly={false}
          />
        </div>
        <br />
        {hasError && (
          <InlineBanner bannerType={InlineBannerType.ERROR} supportingInfo={S3BucketNamingNotice} />
        )}
      </div>
    </Modal>
  );
}

interface S3GatewayModalFieldsProps {
  roleArn: string;
  setGatewayName: (gatewayName: string) => void;
  setBucketName: (bucketName: string) => void;
  gatewayName: string;
  bucketName: string;
  viewOnly: boolean;
}
export function S3GatewayModalFields(props: S3GatewayModalFieldsProps): JSX.Element {
  const {viewOnly, gatewayName, bucketName, setBucketName, setGatewayName} = props;

  return (
    <>
      <TextField
        label="Gateway name"
        isRequired={true}
        value={gatewayName}
        disabled={viewOnly}
        onChange={(e) => {
          setGatewayName(e.target.value);
        }}
      />
      <br />
      <TextField
        label="S3 Bucket name"
        isRequired={true}
        value={bucketName}
        disabled={viewOnly}
        onChange={(e) => {
          setBucketName(e.target.value);
        }}
      />
      <br />
      For additional guidance, check out the{' '}
      <a
        href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/access-policy-language-overview.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        AWS guidelines here.
      </a>
      <br />
    </>
  );
}

export const styles = StyleSheet.create({
  roleArnBox: {
    display: 'flex',
    gridTemplateColumns: '4fr 1fr',
  },
  roleArnField: {
    flex: 1,
  },
  spacer: {
    marginTop: '10px',
  },
  copyButton: {
    width: '97px',
    height: '28px',
    marginLeft: '10px',
    alignSelf: 'flex-end',
    fontSize: '12px',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '0px 16px',
  },
});
